import React, { useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { CreatePostMutation, UpdatePostMutation, GetPostById, GetPostsByUserIdAndStatus } from "../../../services/PostService";
import { postTypeLimitsService } from "../../../services/PostTypeLimitsService";
import { resetPostData, updatePostData } from "../../../Redux/Slice/createPostSlice";
import { fetchServiceCategories } from "../../../Redux/Slice/serviceCategoriesSlice";
import Alerts from "../../../components/common/Alerts/Alerts";
import Spinner from "../../../components/common/Spinner";
import { emitPublicationActivity } from "../../../data/changeHistoryEmitters";
import {
  AdminPageTitle,
  AdminSectionTitle,
  AdminMainText,
  AdminHeaderAction,
  AdminLargeAction,
  AdminFormSecondaryAction,
} from "../../../sections/AdminDashboard/shared/adminUx";
import {
  BUSINESS_WIZARD_SHELL_CLASS,
  BUSINESS_WIZARD_TITLE_CLASS,
  BUSINESS_WIZARD_VIEWPORT_CLASS,
  BUSINESS_WIZARD_COLUMN_CLASS,
  BUSINESS_WIZARD_INNER_CLASS,
} from "../../../sections/BusinessDashboard/shared/businessLayoutSpec";
import PostTypeSelection from "../../../sections/BusinessDashboard/Post/CreatePost/PostTypeSelection";
import DefineCategory from "../../../sections/BusinessDashboard/Post/CreatePost/DefineCategory";
import SelectPostType from "../../../sections/BusinessDashboard/Post/CreatePost/SelectPostType";
import LocationandPortfolio from "../../../sections/BusinessDashboard/Post/CreatePost/LocationandPortfolio";
import PostReview from "../../../sections/BusinessDashboard/Post/CreatePost/PostReview";
import DashboardMainContainer from "../../../components/common/Containers/DashboardMainContainer";
import PostDetails from "../../../sections/BusinessDashboard/Post/CreatePost/PostDetails";
import Keywords from "../../../sections/BusinessDashboard/Post/CreatePost/Keywords";
import PortfolioSection from "../../../sections/BusinessDashboard/Post/CreatePost/PortfolioSection";
// import FinalConfirmation from "../../../sections/BusinessDashboard/Post/CreatePost/FinalConfirmation";
import { useGraphQL } from "../../../context/GraphQLProvider";
import useAuth from "../../../Hooks/useAuth";

import { Formik, Form } from "formik";
import {
  defaultValues,
  validationSchema,
} from "../../../FormValidations/Latest/CreatePost";
import NamedStepper from "../../../components/common/StepIndicater/NamedStepper";
import Tips from "../../../components/common/Tips/Tips";

const CreatePost = () => {
  const navigate = useNavigate();
  const { postId } = useParams();
  const graphqlService = useGraphQL();
  const dispatch = useDispatch();
  const { auth } = useAuth();
  const user = useSelector((state) => state.user);
  const postData = useSelector((state) => state.createPost);
  const { categories } = useSelector((state) => state.serviceCategories);
  
  // Check user plan type
  const isProPlan = auth?.userPlan?.plan?.name?.toLowerCase() === 'pro';
  
  // Set initial step based on user plan - Basic users skip PostTypeSelection
  const [step, setStep] = useState(isProPlan ? 1 : 2);
  const [isLoading, setIsLoading] = useState(false);
  const [postStatus, setPostStatus] = useState(null);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [limitMessage, setLimitMessage] = useState("");
  const [publishedPostsCount, setPublishedPostsCount] = useState(0);

  // Set initial values on mount
  const [initialValues, setInitialValues] = useState(defaultValues);

  // Step names for the stepper - conditional based on user plan
  const stepNames = isProPlan 
    ? [
        "Post Type",
        "Category",
        "Details", 
        "Keywords",
        "Location",
        "Portfolio",
        "Review"
      ]
    : [
        "Category",
        "Details", 
        "Keywords",
        "Location",
        "Portfolio",
        "Review"
      ];

  // Check published posts count and plan limits
  // useEffect(() => {
    const checkPostLimit = async () => {
      if (user.user) {
        try {
          const response = await GetPostsByUserIdAndStatus(graphqlService, {
            userId: parseFloat(user.user.id),
            status: "published"
          });
          const posts = response?.getPostsByStatus || [];
          setPublishedPostsCount(posts.length);

          // Check if user has reached their plan limit
          const userPlan = user.userPlan?.plan?.name?.toLowerCase();
          const postLimit = userPlan === 'basic' ? 3 : userPlan === 'pro' ? 10 : 0;
          
          if (postLimit > 0 && posts.length >= postLimit) {
            setLimitMessage(`You have reached the maximum number of posts (${postLimit}) for your ${userPlan} plan. Please upgrade your plan to post more.`);
            setShowLimitModal(true);
          }
        } catch (error) {
          console.error("Error checking post limit:", error);
        }
      }
    };

  //   checkPostLimit();
  // }, [user.user, user.userPlan, graphqlService, navigate]);

  // Fetch categories if not available
  useEffect(() => {
    if (categories.length === 0) {
      dispatch(fetchServiceCategories(graphqlService));
    }
  }, [dispatch, graphqlService, categories.length]);

  // Load post data if editing
  useEffect(() => {
    const loadPostData = async () => {
      if (postId) {
        try {
          setIsLoading(true);
          const response = await GetPostById(graphqlService, { id: parseInt(postId) });
          if (response?.getPostById) {
            const post = response.getPostById;
            // Store the post status
            setPostStatus(post.status);
            // Parse JSON strings from the post data
            const parsedContent = JSON.parse(post.content || '[]');
            
            // Find the matching category from serviceCategories
            const mainCategoryObj = categories.find(cat => cat.id == post.mainCategory);
            const subCategoryObj = mainCategoryObj?.subcategories.find(cat => cat.id == post.subCategory);
            
            // Update Redux store with post data
            dispatch(updatePostData({
              postId: post.id,
              mainCategory: post.mainCategory,
              subCategory: post.subCategory,
              postTitle: post.postTitle,
              description: post.postDescription,
              postTags: post.postKeywords,
              range: post.postRadius,
              location: post.postSuburb,
              portfolioImages: parsedContent
            }));

            // Set initial values for the form
            setInitialValues({
              ...defaultValues,
              mainCategory: mainCategoryObj,
              subCategory: subCategoryObj,
              postTitle: post.postTitle,
              description: post.postDescription,
              postTags: post.postKeywords || [],
              range: post.postRadius,
              location: post.postSuburb,
              portfolioImages: parsedContent
            });
          }
        } catch (error) {
          console.error('Error loading post:', error);
          Alerts.error('Failed to edit post. Please try again.');
        } finally {
          setIsLoading(false);
        }
      } else {
        // checkPostLimit();
        // Reset post data if creating new post
        // dispatch(resetPostData());
        // setInitialValues(defaultValues);
      }
    };

    loadPostData();
  }, [postId, categories]);

  const handleSubmit = (values) => {
    console.log("Form data:", values);
  };

  const handlePost = async (isPublish = false) => {
    try {
      // Only show loader for publishing
      if (isPublish) {
        setIsLoading(true);
      }
      
      const postPayload = {
        userId: user.user.id,
        author: `${user.firstName} ${user.lastName}`,
        mainCategory: JSON.stringify(postData.mainCategory),
        subCategory: JSON.stringify(postData.subCategory || postData.customSubcategory),
        postTitle: postData.postTitle,
        postDescription: postData.description,
        postKeywords: postData.postTags,
        postRadius: postData.range,
        postSuburb: postData.location,
        content: JSON.stringify(postData.portfolioImages),
        status: isPublish ? 'published' : 'draft',
        isHighlighted: false,
        title: " "
      };

      let response;
      if (postData.postId) {
        // If post exists and is already published, only update if isPublish is true
        if (postStatus === 'published' && !isPublish) {
          return; // Don't update if post is published and we're not publishing
        }
        
        postPayload.id = postData.postId;
        response = await UpdatePostMutation(graphqlService, { input: postPayload });
      } else {
        response = await CreatePostMutation(graphqlService, { input: postPayload });
        // If this is the first save, store the postId
        if (response?.createPost?.id) {
          dispatch(updatePostData({ postId: response.createPost.id }));
        }
      }
      
      if (isPublish) {
        // Update post type limits when publishing
        if (postData.postType && user.user?.id) {
          postTypeLimitsService.updateLimits(user.user.id, postData.postType);
        }

        const publishedId =
          response?.createPost?.id ||
          response?.updatePost?.id ||
          postData.postId;
        if (publishedId) {
          try {
            emitPublicationActivity({
              targetType: 'Post',
              targetId: publishedId,
              targetName: postData.postTitle || publishedId,
              actionLabel: 'Publish',
              outcome: 'Post published successfully',
              actor: `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Business',
            });
          } catch {
            /* history recording failure must not reverse the source save */
          }
        }
        
        // Show success message only for publishing
        Alerts.success('Post Published Successfully!');
        // Reset Redux state
        dispatch(resetPostData());
        // Redirect to published posts page
        navigate("/business-dashboard/posts/published");
      }
    } catch (error) {
      console.error('Error saving post:', error);
      // Show error message only for publishing
      if (isPublish) {
        Alerts.error('Failed to Publish Post. Please try again.');
      }
    } finally {
      // Only hide loader for publishing
      if (isPublish) {
        setIsLoading(false);
      }
    }
  };

  return (
    <>
      <DashboardMainContainer className={BUSINESS_WIZARD_SHELL_CLASS}>
        {isLoading && <Spinner />}
        {showLimitModal && (
          <div className="absolute inset-0 bg-black/35 backdrop-blur-md z-50 justify-center rounded-xl">
            <div className="text-center p-6 mt-32">
              <AdminSectionTitle className="text-white mb-4 text-center">
                Post Limit Reached
              </AdminSectionTitle>
              <AdminMainText className="text-white mb-8 max-w-md mx-auto text-center block">
                {limitMessage}
              </AdminMainText>
              <div className="flex justify-center gap-3">
                <AdminLargeAction
                  onClick={() => navigate("/business-dashboard/subscriptions/pricing")}
                >
                  Upgrade plan
                </AdminLargeAction>
                <AdminFormSecondaryAction
                  onClick={() => navigate("/business-dashboard/posts/published")}
                  className="!bg-white !text-offblack hover:!bg-mistygray"
                >
                  View Posts
                </AdminFormSecondaryAction>
              </div>
            </div>
          </div>
        )}

        <div
          className={BUSINESS_WIZARD_VIEWPORT_CLASS}
          style={{ minHeight: "calc(100vh - 120px)" }}
        >
          <div className={BUSINESS_WIZARD_COLUMN_CLASS}>
            <div className={BUSINESS_WIZARD_INNER_CLASS}>
              <div>
                {step === 1 && isProPlan && (
                  <AdminPageTitle className={BUSINESS_WIZARD_TITLE_CLASS}>Post Type</AdminPageTitle>
                )}
                {step === 2 && (
                  <AdminPageTitle className={BUSINESS_WIZARD_TITLE_CLASS}>Subcategory</AdminPageTitle>
                )}
                {step === 3 && (
                  <AdminPageTitle className={BUSINESS_WIZARD_TITLE_CLASS}>Post Details</AdminPageTitle>
                )}
                {step === 4 && (
                  <AdminPageTitle className={BUSINESS_WIZARD_TITLE_CLASS}>Keywords</AdminPageTitle>
                )}
                {step === 5 && (
                  <AdminPageTitle className={BUSINESS_WIZARD_TITLE_CLASS}>Location</AdminPageTitle>
                )}
                {step === 6 && (
                  <AdminPageTitle className={BUSINESS_WIZARD_TITLE_CLASS}>Images</AdminPageTitle>
                )}
                {step === 7 && (
                  <AdminPageTitle className={BUSINESS_WIZARD_TITLE_CLASS}>Review</AdminPageTitle>
                )}
              </div>

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ setFieldValue, values, validateField, setTouched }) => (
            <Form>
              {/* Render PostTypeSelection component for step 1 - Pro users only */}
              {step === 1 && isProPlan && (
                <PostTypeSelection
                  setStep={setStep}
                  handlePost={handlePost}
                />
              )}

              {/* Render DefineCategory component for step 2 */}
              {step === 2 && (
                <DefineCategory
                  values={values}
                  setFieldValue={setFieldValue}
                  setStep={setStep}
                  handlePost={handlePost}
                />
              )}

              {/* Render PostDetails component for step 3 */}
              {step === 3 && (
                <PostDetails
                  values={values}
                  setFieldValue={setFieldValue}
                  setStep={setStep}
                  handlePost={handlePost}
                />
              )}

              {/* Render Keywords component for step 4 */}
              {step === 4 && (
                <Keywords
                  values={values}
                  setFieldValue={setFieldValue}
                  setStep={setStep}
                  handlePost={handlePost}
                />
              )}

              {/* Render LocationandPortfolio and PortfolioUploading components for step 5 */}
              {step === 5 && (
                <div>
                  <LocationandPortfolio
                    values={values}
                    setFieldValue={setFieldValue}
                    setStep={setStep}
                    handlePost={handlePost}
                  />
                </div>
              )}

              {/* Render PortfolioSection component for step 6 */}
              {step === 6 && (
                <div>
                  <PortfolioSection
                    values={values}
                    setFieldValue={setFieldValue}
                    setStep={setStep}
                    handlePost={handlePost}
                  />
                </div>
              )}

              {/* Render PostReview component for step 7 */}
              {step === 7 && (
                <PostReview 
                  values={values} 
                  setStep={setStep} 
                  handlePost={handlePost}
                />
              )}
            </Form>
          )}
        </Formik>
            </div>
          </div>
        </div>
      </DashboardMainContainer>
    </>
  );
};

export default CreatePost;
